import React, { useEffect, useState, useMemo } from 'react';
import {
    Box, Typography, Divider, Stack, Tooltip, IconButton
} from '@mui/material';

import { EyeOutlined } from '@ant-design/icons';
import { Tag, Input } from 'antd';
import AntTable from '../../../TableResizable';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { ActifDocumentType, extractActifDocumentRows, getActifDocumentLabel, loadActifDocumentTypes, mapActifDocumentsToPreview, toDocumentDataUri } from '../../../../Services/actifDocumentTypes';
import AssetDocumentPreviewModal from '../../../AssetDocumentPreviewModal';
import { useAuthHeader } from '../../../../Services/Auth/sessionAuth';
import axios from 'axios';
import Data from '../../../../Data/Data.json';
import { Crypter } from '../../../../Functions/Security/Crypter';

const buildDocuments = (row: any, definitions: ActifDocumentType[]) => {
    const documents = row.Documents || row.DOCUMENTS || row.documents || [];
    if (documents.length > 0) {
        return documents.map((document: any) => ({
            label: getActifDocumentLabel(document, definitions),
            base64: toDocumentDataUri(document)
        })).filter((document: any) => document.base64);
    }

    // Sinon, créer des entrées factices basées sur NbDocuments pour l'affichage du compteur
    const nbDocuments = row.NBDOCUMENTS || row.NbDocuments || 0;
    if (nbDocuments > 0) {
        return Array.from({ length: nbDocuments }, (_, index) => ({
            label: `Document ${index + 1}`,
            base64: null
        }));
    }

    return [];
};

interface Tab2Props {
    glActifsValides: any[];
    listFinancements: any[];
    disabledForm: boolean;
    form: any;
}

const Tab2 = (props: Tab2Props) => {
    const authHeader = useAuthHeader();
    const [documentDefinitions, setDocumentDefinitions] = useState<ActifDocumentType[]>([]);
    const [search, setSearch] = useState('');
    useEffect(() => {
        loadActifDocumentTypes(authHeader()).then(setDocumentDefinitions).catch(() => setDocumentDefinitions([]));
    }, [authHeader]);
    const [docModal, setDocModal] = useState<{ open: boolean; docs: { label: string; base64: string | null }[]; activeIndex: number | null }>({
        open: false, docs: [], activeIndex: null
    });
    const openDocsModal = (docs: { label: string; base64: string | null }[]) => setDocModal({ open: true, docs, activeIndex: docs.length === 1 ? 0 : null });
    const closeDocsModal = () => setDocModal({ open: false, docs: [], activeIndex: null });

    // Fonction pour charger les documents d'un actif et ouvrir la modal
    const openDocsModalWithLoad = async (row: any) => {
        // Si les documents sont déjà chargés avec leur contenu, les utiliser directement
        const rowDocuments = row.Documents || row.DOCUMENTS || row.documents || [];
        if (rowDocuments.length > 0) {
            const docs = buildDocuments(row, documentDefinitions);
            if (docs.length > 0) {
                setDocModal({ open: true, docs, activeIndex: docs.length === 1 ? 0 : null });
                return;
            }
        }
        // Charger les documents depuis l'API (GetActifWithDocuments si dispo, sinon GetActifsByValidation)
        try {
            const getActifWithDocuments = (Data.ApiUrl as any).GetActifWithDocuments;
            const useNewEndpoint = !!getActifWithDocuments;
            const headers: any = {
                'Authorization': authHeader(),
                'CypherCodeActif': Crypter.encrypt(row.CODEACTIF)
            };
            if (!useNewEndpoint) {
                headers['CypherStatutValidation'] = Crypter.encrypt(row.STATUTVALIDATION || 'VALIDE');
            }
            const response = await axios.get(useNewEndpoint ? getActifWithDocuments : Data.ApiUrl.GetActifsByValidation, { headers });

            const res = JSON.parse(Crypter.decrypt(response.data)).Value;
            if (res.StatutCode === 200) {
                const docRows = extractActifDocumentRows(res.Result, row.CODEACTIF);
                const docs = mapActifDocumentsToPreview(docRows, documentDefinitions);
                setDocModal({ open: true, docs, activeIndex: docs.length === 1 ? 0 : null });
            } else {
                setDocModal({ open: true, docs: [], activeIndex: null });
            }
        } catch (error) {
            console.error('Erreur chargement documents Tab2:', error);
            setDocModal({ open: true, docs: [], activeIndex: null });
        }
    };
    const codesAutorises = props.form?.getFieldValue('FINANCEMENT_ELIGIBLES') || [];

    const actifsFiltrés = props.glActifsValides.filter((row: any) =>
        Object.values(row).some(value => String(value ?? '').toLowerCase().includes(search.toLowerCase()))
    );

    // Libellés des financements éligibles pour la catégorie consultée (Tab1),
    // affichés ici à titre indicatif — même source (form partagé) que Tab1.
    const financementsEligiblesLabels = useMemo(() => {
        return props.listFinancements
            .filter(f => codesAutorises.includes(f.CODE))
            .map(f => f.LIBELLE);
    }, [props.listFinancements, codesAutorises]);

    const formatMontant = (val: number) => new Intl.NumberFormat('fr-FR').format(val || 0);

    const columns: ColumnsType<any> = [
        {
            title: 'Code Actif', key: 'CODEACTIF', width: 100, align: 'center' as const,
            render: (row) => <span style={{ fontWeight: 'bold' }}>{row.CODEACTIF}</span>
        },
        {
            title: 'Catégorie', key: 'CODECATEGORIE', width: 100, align: 'center' as const,
            render: (row) => <span>{row.CODECATEGORIE}</span>
        },
        {
            key: 'IDENTIFIANTUNIQUE', title: 'N° Série / Unique', width: 200,
            render: (row: any) => <span style={{ fontSize: '0.85rem' }}>{row?.IDENTIFIANTUNIQUE}</span>
        },
        {
            title: 'Désignation', key: 'DESIGNATION', width: 200,
            render: (row) => <div className="whitespace-normal break-words">{row.DESIGNATION}</div>
        },
        {
            title: "Coût d'achat(XAF)", key: 'COUTINITIAL', width: 140, align: 'right' as const,
            render: (row) => <span style={{ fontWeight: 'bold' }}>{formatMontant(row.COUTINITIAL)}</span>
        },
        {
            title: "V. Résiduelle", key: 'VALEURRESIDUELLE', width: 130, align: 'right' as const,
            render: (row) => <span>{formatMontant(row.VALEURRESIDUELLE)}</span>
        },
        {
            title: 'Grade', key: 'GRADEDESCRIPTION', width: 80, align: 'center' as const,
            render: (row) => <Tag color="blue">{row.GRADEDESCRIPTION}</Tag>
        },
        {
            title: 'Fournisseur', key: 'CODEFOURNISSEUR', width: 100, align: 'center' as const,
            render: (row) => (
                <Tooltip title={row.LIBELLEFOURNISSEUR || row.lib_CODEFOURNISSEUR}>
                    <span>{row.CODEFOURNISSEUR}</span>
                </Tooltip>
            )
        },
        {
            title: 'Statut', key: 'STATUTVALIDATION', width: 110, align: 'center' as const,
            render: (row) => <Tag color="green">{row?.STATUTVALIDATION}</Tag>
        },
        {
            title: 'Documents', key: 'DOCUMENTS', width: 100, align: 'center' as const,
            render: (row) => {
                const count = row.NBDOCUMENTS ?? row.NbDocuments ?? 0;
                return (
                    <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                        <IconButton size="small" onClick={() => openDocsModalWithLoad(row)} disabled={count === 0 && !row.CODEACTIF}>
                            <EyeOutlined style={{ fontSize: 18, color: count > 0 ? '#2A6596' : '#ccc' }} />
                        </IconButton>
                        <Tag color={count === 0 ? 'default' : 'green'}>{count}</Tag>
                    </Stack>
                );
            }
        },
        {
            title: 'Localisation', key: 'LOCALISATIONCOURANTE', width: 110,
            render: (row) => <span>{row.LOCALISATIONCOURANTE}</span>
        },
        {
            title: 'Saisie par', key: 'ENREGISTREPAR', width: 110,
            render: (row) => (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '0.8rem' }}>{row.ENREGISTREPAR}</span>
                    <span style={{ fontSize: '0.65rem', color: 'gray' }}>
                        {row.DATEENREGISTREMENT ? dayjs(row.DATEENREGISTREMENT).format('DD/MM/YYYY HH:mm') : '-'}
                    </span>
                </Box>
            )
        },
        {
            title: 'Validé par', key: 'VALIDEPAR', width: 110,
            render: (row) => (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '0.8rem', color: '#107C41', fontWeight: 'bold' }}>{row.VALIDEPAR}</span>
                    <span style={{ fontSize: '0.65rem', color: 'gray' }}>
                        {row.DATEVALIDATION ? dayjs(row.DATEVALIDATION).format('DD/MM/YYYY HH:mm') : '-'}
                    </span>
                </Box>
            )
        }
    ];

    return (
        <div className='tabItem'>
            <Box className='border' width={"85%"} position={"absolute"} sx={{ p: 2, bgcolor: '#fff' }}>
                <Box>
                    <Typography variant="h6" fontWeight="bold" color="#04446e">
                        Inventaire des actifs validés et disponibles
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 2 }}>
                    <Stack spacing={1}>
                        {/* AJOUT : financements éligibles pour cette catégorie, à titre indicatif */}
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 1 }}>
                                Financements éligibles pour cette catégorie :
                            </Typography>
                            {financementsEligiblesLabels.length > 0 ? (
                                financementsEligiblesLabels.map((lib: string, idx: number) => (
                                    <Tag key={idx} color="geekblue" style={{ marginBottom: 4 }}>{lib}</Tag>
                                ))
                            ) : (
                                <Typography variant="caption" color="textSecondary">Aucun financement éligible renseigné</Typography>
                            )}
                        </Box>

                        <Typography variant="body2" display="block" color="textSecondary">
                            Actifs disponibles dans cette vue : {actifsFiltrés.length}
                        </Typography>
                    </Stack>

                    
                    <Input size="small" allowClear prefix={<SearchOutlined />} placeholder="Rechercher..." value={search} onChange={event => setSearch(event.target.value)} style={{ width: 220 }} />
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Box sx={{
                    maxHeight: 'calc(100vh - 380px)',
                    //minHeight: '250px',
                    overflowX: 'auto',
                    width: '100%',
                    '& .ant-table-content, & .ant-table-body': {
                        '&::-webkit-scrollbar': { height: '5px', width: '5px' },
                        '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1' },
                        '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: '10px' },
                        '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#2A6596' },
                    }
                }}>
                    <AntTable
                        dataSource={actifsFiltrés}
                        columns={columns}
                        rowKey={(record) => record.CODEACTIF}
                        size="small"
                        pagination={false}
                        sticky={true}
                        scroll={{ x: 'max-content' }}
                    />
                </Box>
            </Box>

            <AssetDocumentPreviewModal
                open={docModal.open}
                docs={docModal.docs}
                activeIndex={docModal.activeIndex}
                onSelect={(index) => setDocModal(prev => ({ ...prev, activeIndex: index }))}
                onBack={() => setDocModal(prev => ({ ...prev, activeIndex: null }))}
                onClose={closeDocsModal}
            />
        </div>
    );
};

export default Tab2;